import "./ClientDetailsFormComponent.scss";
import _ from "lodash";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormDebuggerComponent from "../../shared/components/form-debugger/FormDebuggerComponent";
import FormikInputComponent from "../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikPhoneInputComponent from "../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import {CommonService} from "../../shared/services";
import ButtonComponent from "../../shared/components/button/ButtonComponent";
import FormikDatePickerComponent
    from "../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormikSelectDropdownComponent
    from "../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../store/reducers";
import ToolTipComponent from "../../shared/components/tool-tip/ToolTipComponent";
import {ImageConfig, Misc} from "../../constants";
import IconButtonComponent from "../../shared/components/icon-button/IconButtonComponent";
import FormikRadioButtonGroupComponent
    from "../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";
import {IAPIResponseType} from "../../shared/models/api.model";
import {IClientBasicDetails} from "../../shared/models/client.model";
import {getAccessibleAccounts} from "../../store/actions/static-data.action";
import commonService from "../../shared/services/common.service";
import moment from "moment";

interface ClientDetailsFormComponentProps {
    setIsClientDetailsFormDrawerOpen: any
}

const clientFormInitialValue: any = {
    first_name: '',
    last_name: '',
    dob: '',
    gender: '',
    relationship: '',
    primary_email: '',
    primary_contact_info: {
        phone: ''
    },
    is_dependent: true,
}

const clientFormValidationSchema = Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    dob: Yup.mixed().required('Date of birth is required'),
    gender: Yup.string().required('Gender is required'),
    relationship: Yup.string().required('Relationship is required'),
    primary_email: Yup.string()
        .email('Invalid email format')
        .when('is_dependent', {
            is: false,
            then: Yup.string().required('Primary email is required')
        }),
    primary_contact_info: Yup.object().shape({
        phone: Yup.string().when('is_dependent', {
            is: false,
            then: Yup.string().required('Phone number is required')
        })
    }),
    is_dependent: Yup.boolean().required('Dependency status is required')
});

const ClientDetailsFormComponent = (props: ClientDetailsFormComponentProps) => {

    const {setIsClientDetailsFormDrawerOpen} = props;
    const dispatch = useDispatch();
    const [clientDetailsFormInitialValues] = useState<any>(_.cloneDeep(clientFormInitialValue));
    const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
    const {
        genderList,
        isGenderListLoaded,
        isGenderListLoading,
        relationshipList,
    } = useSelector((state: IRootReducerState) => state.staticData);

    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        const payload = {...values};
        setIsFormSubmitting(true)
        payload['dob'] = CommonService.convertDateFormat(payload['dob']);
        CommonService._client.ClientAddDetails(payload)
            .then((response: IAPIResponseType<IClientBasicDetails>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                commonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: "accountsICanAccess"
                });
                setIsFormSubmitting(false);
                setIsClientDetailsFormDrawerOpen(false)
                dispatch(getAccessibleAccounts());
            }).catch((error: any) => {
            CommonService.handleErrors(setErrors, error, true);
            setIsFormSubmitting(false)
        });
    }, [dispatch, setIsClientDetailsFormDrawerOpen]);

    return (
        <div className={'client-details-form-component'}>
            <div className={'client-details-header'}>New Client Details</div>
            <div className={'client-info-header'}>Please enter the client’s information below:</div>
            <Formik
                validationSchema={clientFormValidationSchema}
                initialValues={clientDetailsFormInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onSubmit}
            >
                {({values, validateForm, isValid, setFieldValue, isSubmitting, dirty}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values, dirty]);
                    return (
                        <Form className="t-form" noValidate={true}>
                            <FormDebuggerComponent values={values} showDebugger={false}/>
                            <div className="request-access-form-controls">
                                <Field name={'first_name'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                id={'client_first_name'}
                                                label={'First Name'}
                                                placeholder={'Eg., John'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                                fullWidth={true}
                                            />
                                        )
                                    }
                                </Field>
                                <Field name={'last_name'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                id={'client_last_name'}
                                                label={'Last Name'}
                                                placeholder={'Eg., Doe'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                                fullWidth={true}
                                            />
                                        )
                                    }
                                </Field>
                                <Field name={'dob'} className="t-form-control">
                                    {
                                        (field: FieldProps) => (
                                            <FormikDatePickerComponent
                                                id={'dob_input'}
                                                label={'Date of Birth'}
                                                placeholder={'MM-DD-YYYY'}
                                                required={true}
                                                formikField={field}
                                                fullWidth={true}
                                                // clearable={true}
                                                maxDate={moment()}
                                            />
                                        )
                                    }
                                </Field>
                                <Field name={'gender'} className="t-form-control">
                                    {
                                        (field: FieldProps) => (
                                            <FormikSelectDropdownComponent
                                                id={'gender_dd'}
                                                label={'Gender'}
                                                options={genderList}
                                                isDataLoading={isGenderListLoading}
                                                isDataLoaded={isGenderListLoaded}
                                                placeholder={'Eg., Male'}
                                                required={true}
                                                isClearable={false}
                                                formikField={field}
                                                fullWidth={true}
                                            />
                                        )
                                    }
                                </Field>
                                <Field
                                    name={'relationship'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikSelectDropdownComponent
                                                options={relationshipList}
                                                label={'Relationship to the client'}
                                                placeholder={'Eg., Manager'}
                                                required={true}
                                                formikField={field}
                                                fullWidth={true}
                                                isClearable={false}
                                            />
                                        )
                                    }
                                </Field>
                                <div className="form-question">
                                    <div className="form-question-text">
                                        Is this profile for a dependent?
                                        <IconButtonComponent
                                            className={"form-helper-icon mrg-left-5 mrg-right-100"}>
                                            <ToolTipComponent
                                                showArrow={true}
                                                position={"bottom"}
                                                tooltip={<>
                                                    A <b>dependent profile</b> is an account created for someone who is
                                                    unable
                                                    to manage their own account due to age or physical/mental
                                                    challenges. Typically, this profile is linked to the main account of
                                                    an individual (like a parent or guardian), who has control over the
                                                    account's settings and activity. Dependent profiles are often used
                                                    for children under 18 or individuals with disabilities, allowing the
                                                    responsible party to monitor or limit access to certain content or
                                                    features. This account will be under your email address and cell
                                                    phone number.
                                                </>}>
                                                <ImageConfig.InfoFilledIcon className={'info-icon'}/>
                                            </ToolTipComponent>
                                        </IconButtonComponent>
                                    </div>
                                    <div>
                                        <Field name={'is_dependent'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikRadioButtonGroupComponent
                                                        options={CommonService._staticData.yesNoOptions}
                                                        required={true}
                                                        displayWith={(option) => option.title}
                                                        valueExtractor={(option) => option.code}
                                                        formikField={field}
                                                        onChange={(value: any) => {
                                                            if (value === true) {
                                                                setFieldValue(' _primary_email', '');
                                                                setFieldValue('_primary_contact_info._phone', '')
                                                            }
                                                        }}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                {
                                    values?.is_dependent === false && <>

                                        <Field name={'primary_email'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={'email_input'}
                                                        label={'Email Address'}
                                                        placeholder={'example@email.com'}
                                                        type={"email"}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'primary_contact_info.phone'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikPhoneInputComponent
                                                        id={'phone_input'}
                                                        label={'Cell Phone Number'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>

                                    </>
                                }
                            </div>

                            <div className="t-form-actions">
                                <ButtonComponent
                                    color={'accent'}
                                    variant={'outlined'}
                                    onClick={() => {
                                        setIsClientDetailsFormDrawerOpen(false)
                                    }}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    color={'accent'}
                                    type={'submit'}
                                    disabled={!isValid}
                                    className={'mrg-left-10'}
                                    isLoading={isFormSubmitting}
                                    variant={'contained'}>
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </div>
    )
        ;

};

export default ClientDetailsFormComponent;
